/**
 * Charts panel for the optimization detail page (R2 visualization layer).
 *
 * Renders heatmap, distribution histogram, scatter plot, and per-param
 * stability sparklines from server-side aggregations of the OptimizationCombo
 * table. Server returns ~100-cell heatmaps / 50-bin histograms / 5K scatter
 * points instead of raw 840K combos — visualizations stay snappy regardless
 * of grid size.
 *
 * Defaults are dynamic per-strategy + per-run:
 *   - metric: blended_return when idle != cash else total_return
 *   - x_param / y_param: most-varied numeric non-bool params from the run's
 *     param_ranges (NOT hardcoded — adapts to whatever strategy was used)
 *
 * 3D toggles (Surface3D / Scatter3D) come in Phase C — placeholders below.
 */
import React, { useEffect, useState } from 'react';
import Plot from 'react-plotly.js';
import { tradingService } from '../../../services/tradingService';

const fmtNum = (n, d = 2) => (n == null || isNaN(n) ? '—' : Number(n).toFixed(d));

// Plotly theme — matches the rest of the app's dark/gold aesthetic.
// Reading body class for theme since we don't have ThemeContext imported here.
const isDarkTheme = () =>
  typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

const themeLayout = () => {
  const dark = isDarkTheme();
  return {
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    font: {
      family: 'Inter, system-ui, sans-serif',
      color: dark ? '#e5e7eb' : '#374151',
      size: 11,
    },
    margin: { l: 50, r: 20, t: 30, b: 50 },
    xaxis: { gridcolor: dark ? 'rgba(75,85,99,0.3)' : 'rgba(229,231,235,0.8)' },
    yaxis: { gridcolor: dark ? 'rgba(75,85,99,0.3)' : 'rgba(229,231,235,0.8)' },
  };
};

// Brand-gold sequential colorscale for heatmaps. Anchors at:
//   negative — red, zero — gray, positive — gold/green
// Uses a continuous scale (Plotly's `colorscale: [[0, c0], ...]`).
const BRAND_HEATMAP_SCALE = [
  [0.0, '#ef4444'],   // red (worst)
  [0.45, '#374151'],  // dark gray
  [0.5, '#6b7280'],   // mid gray
  [0.55, '#a88a4e'],  // gold-dark
  [0.75, '#c9a96e'],  // brand gold
  [1.0, '#22c55e'],   // green (best)
];

const PlotlyConfig = {
  displaylogo: false,
  modeBarButtonsToRemove: ['lasso2d', 'select2d', 'autoScale2d'],
  responsive: true,
};

// Pretty-print metric / param names for display.
//   total_return → Total Return
//   blended_max_drawdown → Blended Max Drawdown
//   rsi_buy → rsi_buy (params kept as-is — they're code-style identifiers)
const PRETTY_METRIC = {
  total_return: 'Total Return',
  sharpe_ratio: 'Sharpe Ratio',
  sortino_ratio: 'Sortino Ratio',
  calmar_ratio: 'Calmar Ratio',
  max_drawdown: 'Max Drawdown',
  win_rate: 'Win Rate',
  profit_factor: 'Profit Factor',
  num_trades: 'Trade Count',
  blended_return: 'Blended Return',
  blended_sharpe: 'Blended Sharpe',
  blended_max_drawdown: 'Blended Max Drawdown',
};
const prettyMetric = (name) => PRETTY_METRIC[name] || name;

// ─────────────────────────────────────────────────────────────────────────
// Surface3D: same data as heatmap, but rendered as a Plotly 3D surface.
// Lets you rotate to see param sensitivity from any angle. Useful for
// spotting saddle points / ridges that 2D heatmaps flatten.
// ─────────────────────────────────────────────────────────────────────────
const Surface3DView = ({ data }) => {
  if (!data || !data.data || data.data.length === 0) {
    return <div className="text-xs text-gray-500 py-8 text-center">No surface data.</div>;
  }
  const xName = data.x_field || data.x_param;
  const yName = data.y_field || data.y_param;
  const zName = data.z_field || data.metric;
  const xPretty = prettyMetric(xName);
  const yPretty = prettyMetric(yName);
  const zPretty = prettyMetric(zName);
  // Plotly's surface needs numeric x/y arrays. The labels are stringified
  // numbers (param values OR bin centers) — coerce numerically; fall back
  // to integer indices when the label isn't parseable.
  const numericOr = (s, i) => {
    const n = parseFloat(s);
    return isNaN(n) ? i : n;
  };
  const xs = data.x_labels.map(numericOr);
  const ys = data.y_labels.map(numericOr);
  // Auto-fit the scene to the actual data range so the cube doesn't show
  // empty space around a clustered surface. Min/max come from the labels
  // (which are param values OR bin centers — both are numeric).
  const numericXs = xs.filter(v => typeof v === 'number' && !isNaN(v));
  const numericYs = ys.filter(v => typeof v === 'number' && !isNaN(v));
  const xRange = numericXs.length ? [Math.min(...numericXs), Math.max(...numericXs)] : undefined;
  const yRange = numericYs.length ? [Math.min(...numericYs), Math.max(...numericYs)] : undefined;
  return (
    <Plot
      data={[{
        type: 'surface',
        z: data.data,
        x: xs,
        y: ys,
        colorscale: BRAND_HEATMAP_SCALE,
        cmid: 0,
        contours: { z: { show: true, usecolormap: true, highlightcolor: '#c9a96e', project: { z: true } } },
        hovertemplate: `${xPretty}: %{x}<br>${yPretty}: %{y}<br>avg ${zPretty}: %{z:.2f}<extra></extra>`,
        colorbar: { title: { text: zPretty, side: 'right' }, thickness: 12 },
        connectgaps: true,
      }]}
      layout={{
        ...themeLayout(),
        title: { text: `${zPretty} surface over ${xPretty} × ${yPretty}`, font: { size: 13 } },
        height: 520,
        scene: {
          xaxis: { title: { text: xPretty }, ...(xRange && { range: xRange }) },
          yaxis: { title: { text: yPretty }, ...(yRange && { range: yRange }) },
          zaxis: { title: { text: zPretty } },
          camera: { eye: { x: 1.6, y: 1.6, z: 0.85 } },
          aspectmode: 'cube',
        },
      }}
      config={PlotlyConfig}
      useResizeHandler
      style={{ width: '100%' }}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Scatter3D: drawdown (X), return (Y), sharpe (Z) for the same sample
// of combos that Scatter (2D) uses. Color encodes Sharpe too — gives
// instant visual depth to "is the high-Sharpe corner also the low-DD,
// high-Return corner?"
// ─────────────────────────────────────────────────────────────────────────
const Scatter3DView = ({ data }) => {
  if (!data || !data.points || data.points.length === 0) {
    return <div className="text-xs text-gray-500 py-8 text-center">No scatter data.</div>;
  }
  const xs = data.points.map(p => p.x);
  const ys = data.points.map(p => p.y);
  const zs = data.points.map(p => p.c ?? 0);  // Sharpe on Z axis
  return (
    <Plot
      data={[{
        type: 'scatter3d',
        mode: 'markers',
        x: xs,
        y: ys,
        z: zs,
        marker: {
          size: 2,
          color: zs,
          colorscale: BRAND_HEATMAP_SCALE,
          colorbar: { title: { text: prettyMetric(data.color_label), side: 'right' }, thickness: 12 },
          line: { width: 0 },
          opacity: 0.6,
        },
        hovertemplate: `${prettyMetric(data.x_label)}: %{x:.2f}<br>${prettyMetric(data.y_label)}: %{y:.2f}<br>${prettyMetric(data.color_label)}: %{z:.2f}<extra></extra>`,
      }]}
      layout={{
        ...themeLayout(),
        title: { text: `3D: ${prettyMetric(data.x_label)} × ${prettyMetric(data.y_label)} × ${prettyMetric(data.color_label)}`, font: { size: 13 } },
        height: 480,
        scene: {
          xaxis: { title: prettyMetric(data.x_label) },
          yaxis: { title: prettyMetric(data.y_label) },
          zaxis: { title: prettyMetric(data.color_label) },
          camera: { eye: { x: 1.5, y: 1.5, z: 0.8 } },
        },
      }}
      config={PlotlyConfig}
      useResizeHandler
      style={{ width: '100%' }}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Mesh3D: a TRUE surface through every individual combo — no averaging,
// no binning. Plotly's mesh3d Delaunay-triangulates the (x, y) point cloud
// and uses each combo's actual color metric as the Z height. Peak combos
// stay at their real values (so e.g. +42k return shows as a tall spike,
// not a buried average). Trade-off: with dense overlap on (x, y) the
// triangulation can look spiky; that's what "see all points" really looks
// like.
// ─────────────────────────────────────────────────────────────────────────
const Mesh3DView = ({ data }) => {
  if (!data || !data.points || data.points.length === 0) {
    return <div className="text-xs text-gray-500 py-8 text-center">No surface data.</div>;
  }
  const xs = data.points.map(p => p.x);
  const ys = data.points.map(p => p.y);
  const zs = data.points.map(p => p.c ?? 0);  // height = color metric (e.g., return)
  const xLabel = prettyMetric(data.x_label);
  const yLabel = prettyMetric(data.y_label);
  const zLabel = prettyMetric(data.color_label);
  return (
    <Plot
      data={[{
        type: 'mesh3d',
        x: xs,
        y: ys,
        z: zs,
        intensity: zs,
        colorscale: BRAND_HEATMAP_SCALE,
        cmid: 0,
        // Triangulate in the XY plane — produces a 2D-manifold surface
        // whose height is each point's actual Z value (no averaging).
        delaunayaxis: 'z',
        opacity: 0.92,
        flatshading: false,
        colorbar: { title: { text: zLabel, side: 'right' }, thickness: 12 },
        hovertemplate: `${xLabel}: %{x:.2f}<br>${yLabel}: %{y:.2f}<br>${zLabel}: %{z:.2f}<extra></extra>`,
      }]}
      layout={{
        ...themeLayout(),
        title: {
          text: `${zLabel} surface — all ${xs.length.toLocaleString()} combos (no averaging)`,
          font: { size: 13 },
        },
        height: 540,
        scene: {
          xaxis: { title: { text: xLabel } },
          yaxis: { title: { text: yLabel } },
          zaxis: { title: { text: zLabel } },
          camera: { eye: { x: 1.6, y: 1.6, z: 0.85 } },
          aspectmode: 'cube',
        },
      }}
      config={PlotlyConfig}
      useResizeHandler
      style={{ width: '100%' }}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Heatmap: 2D grid of avg(metric) per (x_param, y_param) cell.
// Each cell's color = avg metric across all combos sharing that (x, y) pair.
// Hover shows the param values + metric + n.
// ─────────────────────────────────────────────────────────────────────────
const HeatmapView = ({ data }) => {
  if (!data || !data.data || data.data.length === 0) {
    return <div className="text-xs text-gray-500 py-8 text-center">No heatmap data — try different axes.</div>;
  }
  // Prefer the unified field names from the new backend; fall back to legacy
  // keys (x_param/y_param/metric) for older runs / cached responses.
  const xName = data.x_field || data.x_param;
  const yName = data.y_field || data.y_param;
  const zName = data.z_field || data.metric;
  const xPretty = prettyMetric(xName);
  const yPretty = prettyMetric(yName);
  const zPretty = prettyMetric(zName);
  return (
    <Plot
      data={[{
        type: 'heatmap',
        z: data.data,
        x: data.x_labels,
        y: data.y_labels,
        colorscale: BRAND_HEATMAP_SCALE,
        zmid: 0,
        hovertemplate: `${xPretty}: %{x}<br>${yPretty}: %{y}<br>avg ${zPretty}: %{z:.2f}<extra></extra>`,
        colorbar: { title: { text: zPretty, side: 'right' }, thickness: 12 },
      }]}
      layout={{
        ...themeLayout(),
        title: { text: `Avg ${zPretty} per (${xPretty} × ${yPretty})`, font: { size: 13 } },
        xaxis: { ...themeLayout().xaxis, title: { text: xPretty }, type: 'category' },
        yaxis: { ...themeLayout().yaxis, title: { text: yPretty }, type: 'category' },
        height: 380,
      }}
      config={PlotlyConfig}
      useResizeHandler
      style={{ width: '100%' }}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Histogram: distribution of the chosen metric across all combos.
// Server returns 50 pre-computed bins.
// ─────────────────────────────────────────────────────────────────────────
const HistogramView = ({ data }) => {
  if (!data || !data.bins || data.bins.length === 0) {
    return <div className="text-xs text-gray-500 py-8 text-center">No histogram data.</div>;
  }
  const xCenters = data.bins.map(b => (b.lo + b.hi) / 2);
  const counts = data.bins.map(b => b.count);
  return (
    <Plot
      data={[{
        type: 'bar',
        x: xCenters,
        y: counts,
        marker: { color: '#c9a96e', line: { color: '#a88a4e', width: 0.5 } },
        hovertemplate: `${data.metric}: %{x:.2f}<br>combos: %{y}<extra></extra>`,
      }]}
      layout={{
        ...themeLayout(),
        title: { text: `Distribution of ${data.metric}`, font: { size: 13 } },
        xaxis: { ...themeLayout().xaxis, title: { text: data.metric } },
        yaxis: { ...themeLayout().yaxis, title: { text: 'combos' } },
        bargap: 0.05,
        height: 280,
        shapes: data.stats?.median != null ? [{
          type: 'line',
          x0: data.stats.median,
          x1: data.stats.median,
          y0: 0, y1: Math.max(...counts),
          line: { color: '#22c55e', width: 1, dash: 'dot' },
        }] : [],
      }}
      config={PlotlyConfig}
      useResizeHandler
      style={{ width: '100%' }}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Scatter: drawdown (X) vs return (Y), colored by Sharpe.
// Lets you eyeball "what's the best return-per-drawdown trade-off?"
// (Pareto front highlighting comes in Phase D.)
// ─────────────────────────────────────────────────────────────────────────
const ScatterView = ({ data }) => {
  if (!data || !data.points || data.points.length === 0) {
    return <div className="text-xs text-gray-500 py-8 text-center">No scatter data.</div>;
  }
  // Split points into Pareto-optimal (highlighted) and the rest.
  // Pareto front in (drawdown, return) space = the non-dominated combos
  // — for any other combo, you can find at least one Pareto point that's
  // both higher-return AND less-bad-drawdown. These are the strategies
  // worth picking from; everything else is strictly worse on both axes.
  const paretoSet = new Set(data.pareto_idx || []);
  const nonPareto = [];
  const pareto = [];
  data.points.forEach((p, i) => {
    if (paretoSet.has(i)) pareto.push(p);
    else nonPareto.push(p);
  });
  const traces = [
    {
      type: 'scatter',
      mode: 'markers',
      name: 'all combos',
      x: nonPareto.map(p => p.x),
      y: nonPareto.map(p => p.y),
      marker: {
        size: 4,
        color: nonPareto.map(p => p.c ?? 0),
        colorscale: BRAND_HEATMAP_SCALE,
        colorbar: { title: { text: prettyMetric(data.color_label), side: 'right' }, thickness: 12 },
        line: { width: 0 },
        opacity: 0.5,
      },
      hovertemplate: `${prettyMetric(data.x_label)}: %{x:.2f}<br>${prettyMetric(data.y_label)}: %{y:.2f}<br>${prettyMetric(data.color_label)}: %{marker.color:.2f}<extra></extra>`,
    },
  ];
  if (pareto.length > 0) {
    // Sort Pareto points by x (drawdown) so the connecting line traces
    // the front from worst-DD to least-bad-DD.
    const sortedPareto = [...pareto].sort((a, b) => a.x - b.x);
    traces.push({
      type: 'scatter',
      mode: 'lines+markers',
      name: `Pareto front (${pareto.length})`,
      x: sortedPareto.map(p => p.x),
      y: sortedPareto.map(p => p.y),
      line: { color: '#c9a96e', width: 2, dash: 'dash' },
      marker: {
        size: 9,
        color: '#c9a96e',
        line: { color: '#a88a4e', width: 1.5 },
        symbol: 'star',
      },
      hovertemplate: `★ Pareto-optimal<br>${prettyMetric(data.x_label)}: %{x:.2f}<br>${prettyMetric(data.y_label)}: %{y:.2f}<extra></extra>`,
    });
  }
  return (
    <Plot
      data={traces}
      layout={{
        ...themeLayout(),
        title: {
          text: `${prettyMetric(data.y_label)} vs ${prettyMetric(data.x_label)} (color: ${prettyMetric(data.color_label)})`
                + (data.sampled ? ` — smart sample: ${(data.sample_n ?? data.points.length).toLocaleString()} of ${data.total.toLocaleString()} combos, Pareto exact` : ''),
          font: { size: 13 },
        },
        xaxis: { ...themeLayout().xaxis, title: { text: prettyMetric(data.x_label) } },
        yaxis: { ...themeLayout().yaxis, title: { text: prettyMetric(data.y_label) } },
        height: 360,
        showlegend: pareto.length > 0,
        legend: { x: 0.02, y: 0.98, bgcolor: 'rgba(0,0,0,0)' },
      }}
      config={PlotlyConfig}
      useResizeHandler
      style={{ width: '100%' }}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Stability sparklines: per-param avg(metric) as the param value sweeps.
// A flat line = robust (param doesn't matter much — good sign).
// A peak = sweet spot exists. A monotone slope = clear preference.
// ─────────────────────────────────────────────────────────────────────────
const StabilitySpark = ({ name, points, metric }) => {
  if (!points || points.length === 0) return null;
  const xs = points.map(p => p.x);
  const ys = points.map(p => p.y);
  return (
    <div className="border border-gray-200 dark:border-gray-800 rounded-md p-2 bg-white/30 dark:bg-gray-900/30">
      <div className="flex items-baseline justify-between mb-1">
        <div className="text-[10px] font-mono text-gray-700 dark:text-gray-300">{name}</div>
        <div className="text-[9px] text-gray-500">
          range {fmtNum(Math.min(...ys))}…{fmtNum(Math.max(...ys))}
        </div>
      </div>
      <Plot
        data={[{
          type: 'scatter',
          mode: 'lines+markers',
          x: xs,
          y: ys,
          line: { color: '#c9a96e', width: 2 },
          marker: { size: 4, color: '#c9a96e' },
          hovertemplate: `${name}: %{x}<br>avg ${metric}: %{y:.2f}<extra></extra>`,
        }]}
        layout={{
          ...themeLayout(),
          margin: { l: 30, r: 5, t: 5, b: 20 },
          height: 70,
          xaxis: { ...themeLayout().xaxis, title: '', showgrid: false, tickfont: { size: 9 } },
          yaxis: { ...themeLayout().yaxis, title: '', showgrid: false, tickfont: { size: 9 } },
          showlegend: false,
        }}
        config={{ ...PlotlyConfig, displayModeBar: false, staticPlot: false }}
        useResizeHandler
        style={{ width: '100%' }}
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Main ChartsPanel — fetches /visualize/ data once on mount, re-fetches
// when the user changes metric / x_param / y_param via the dropdowns.
//
// view prop controls which chart to render:
//   'heatmap'     → Heatmap card (with 2D/3D toggle)
//   'distribution'→ Histogram card
//   'scatter'     → Scatter card (with 2D/3D toggle + Pareto front)
//   'stability'   → Per-param sparklines grid
//   undefined / 'all' → all four (legacy "everything in one panel" layout)
// ─────────────────────────────────────────────────────────────────────────
const ChartsPanel = ({ runId, run, view = 'all' }) => {
  const [vizData, setVizData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Global metric: drives histogram, stability, and the heatmap's Z when
  // zField hasn't been overridden. null = use server default.
  const [metric, setMetric] = useState(null);
  // Heatmap/Surface axes — any field (param OR metric). zField is the
  // height/color and must be a metric. When null, defaults to `metric`.
  const [xField, setXField] = useState(null);
  const [yField, setYField] = useState(null);
  const [zField, setZField] = useState(null);
  // Per-chart view-mode toggles.
  //   heatmap3d:   heatmap card → 2D heatmap or 3D Surface (binned aggregate)
  //   scatterMode: scatter card → '2d' (Pareto), '3d' (point cloud), or
  //                'surface' (mesh3d through every point — no averaging).
  const [heatmap3d, setHeatmap3d] = useState(false);
  const [scatterMode, setScatterMode] = useState('2d');
  // Scatter axis selectors — any metric for X, Y, or color.
  const [scatterX, setScatterX] = useState(null);
  const [scatterY, setScatterY] = useState(null);
  const [scatterC, setScatterC] = useState(null);
  // Track what's currently RENDERED so we know if dropdown changes are
  // unapplied. This decouples selector updates from fetches — the user
  // edits freely and explicitly clicks Reload to recompute. Each entry
  // mirrors one of the draft state fields above.
  const [applied, setApplied] = useState({
    metric: null, xField: null, yField: null, zField: null,
    scatterX: null, scatterY: null, scatterC: null,
  });
  // Async compute state — shown while Celery task is running on the server.
  const [computing, setComputing] = useState(false);
  const computingRef = React.useRef(false);

  const _applyVizData = (data) => {
    setVizData(data);
    const sel = data.selected || {};
    const def = data.defaults || {};
    const finalMetric = metric   || sel.metric   || def.metric;
    const finalXField = xField   || def.x_field  || sel.x_param;
    const finalYField = yField   || def.y_field  || sel.y_param;
    const finalZField = zField   || def.z_field  || sel.metric;
    const finalSX = scatterX || data.scatter?.x_label;
    const finalSY = scatterY || data.scatter?.y_label;
    const finalSC = scatterC || data.scatter?.color_label;
    if (!metric)   setMetric(finalMetric);
    if (!xField)   setXField(finalXField);
    if (!yField)   setYField(finalYField);
    if (!zField)   setZField(finalZField);
    if (!scatterX) setScatterX(finalSX);
    if (!scatterY) setScatterY(finalSY);
    if (!scatterC) setScatterC(finalSC);
    setApplied({
      metric: finalMetric, xField: finalXField, yField: finalYField, zField: finalZField,
      scatterX: finalSX, scatterY: finalSY, scatterC: finalSC,
    });
  };

  const fetchViz = async ({ force = false } = {}) => {
    setLoading(true);
    setError(null);
    setComputing(false);
    computingRef.current = false;
    try {
      const body = {};
      if (metric) body.metric = metric;
      if (xField) body.x_field = xField;
      if (yField) body.y_field = yField;
      if (zField) body.z_field = zField;
      if (scatterX) body.x_metric = scatterX;
      if (scatterY) body.y_metric = scatterY;
      if (scatterC) body.color_metric = scatterC;
      if (force) body.force = true;

      const res = await tradingService.visualizeOptimization(runId, body);

      // 202 = task queued or already computing — start polling.
      if (res.status === 202) {
        setLoading(false);
        setComputing(true);
        computingRef.current = true;
        // Poll every 2s re-issuing the same body (cache hit returns 200).
        const poll = async () => {
          if (!computingRef.current) return;
          try {
            const p = await tradingService.visualizeOptimization(runId, body);
            if (p.status === 200) {
              setComputing(false);
              computingRef.current = false;
              _applyVizData(p.data);
            } else if (p.data?.error) {
              setComputing(false);
              computingRef.current = false;
              setError(p.data.error);
            } else {
              setTimeout(poll, 2000);
            }
          } catch (err) {
            setComputing(false);
            computingRef.current = false;
            setError(err.response?.data?.error || err.message);
          }
        };
        setTimeout(poll, 2000);
        return;
      }

      // 200 = data ready (cache hit or small run computed inline).
      _applyVizData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  // Stop polling if the component unmounts (user navigates away).
  React.useEffect(() => {
    return () => { computingRef.current = false; };
  }, []);

  // Fetch on mount / when the user navigates between optimization runs.
  // Selector changes do NOT auto-fetch — user clicks Reload to apply.
  useEffect(() => {
    fetchViz();
    // eslint-disable-next-line
  }, [runId]);

  // Compare draft selections to last-applied to surface the "unapplied
  // changes" hint on the Reload button. Only fields relevant to the
  // current view count — flipping a scatter selector while on the heatmap
  // tab shouldn't make the Reload button light up.
  const isDirty = (() => {
    if (view === 'heatmap' || view === 'all') {
      if (xField !== applied.xField) return true;
      if (yField !== applied.yField) return true;
      if ((zField || metric) !== (applied.zField || applied.metric)) return true;
    }
    if (view === 'scatter' || view === 'all') {
      if (scatterX !== applied.scatterX) return true;
      if (scatterY !== applied.scatterY) return true;
      if (scatterC !== applied.scatterC) return true;
    }
    if (view === 'histogram' || view === 'stability' || view === 'all') {
      if (metric !== applied.metric) return true;
    }
    return false;
  })();

  if (!run?.combos_streamed) {
    return (
      <div className="p-4 text-xs text-gray-500 bg-gray-50 dark:bg-gray-900/30 rounded">
        Charts only available for runs with per-combo data (created on/after 2026-04-26).
      </div>
    );
  }

  const params = vizData?.available_params || [];
  const metrics = vizData?.available_metrics || [];
  const fields = vizData?.available_fields || [...params, ...metrics];

  // Render a styled <select> with pretty labels for metrics, raw names for
  // params (since param names like 'rsi_buy' are code-style identifiers).
  const FieldSelect = ({ label, value, onChange, options, disabled }) => (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-gray-500 mb-1 block">{label}</label>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || options.length === 0}
        className="px-2 py-1.5 text-xs rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#0d1117] min-w-[140px]"
      >
        {options.map(name => (
          <option key={name} value={name}>{prettyMetric(name)}</option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Selectors row — view-aware so each tab shows only the dropdowns
          that affect its chart. Heatmap/Surface needs X/Y/Z fields (any
          param or metric for X/Y, metric for Z). Scatter needs three
          metric selectors. Histogram + Stability need just one metric. */}
      <div className="flex flex-wrap items-end gap-3 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-900/30">
        {(view === 'heatmap' || view === 'all') && (
          <>
            <FieldSelect label="X axis" value={xField} onChange={setXField} options={fields} disabled={loading} />
            <FieldSelect label="Y axis" value={yField} onChange={setYField} options={fields} disabled={loading} />
            <FieldSelect label="Z (height/color)" value={zField || metric} onChange={setZField} options={metrics} disabled={loading} />
          </>
        )}
        {view === 'scatter' && (
          <>
            <FieldSelect label="X axis (metric)" value={scatterX} onChange={setScatterX} options={metrics} disabled={loading} />
            <FieldSelect label="Y axis (metric)" value={scatterY} onChange={setScatterY} options={metrics} disabled={loading} />
            <FieldSelect label="Color (metric)" value={scatterC} onChange={setScatterC} options={metrics} disabled={loading} />
          </>
        )}
        {(view === 'histogram' || view === 'stability') && (
          <FieldSelect label="Metric" value={metric} onChange={setMetric} options={metrics} disabled={loading} />
        )}
        {/* Reload — gold-tinted when there are unapplied dropdown changes,
            grey otherwise. Always usable as an "apply" button. */}
        <button
          type="button"
          onClick={() => fetchViz({ force: false })}
          disabled={loading || computing}
          className={`px-3 py-1.5 text-[11px] uppercase tracking-wider rounded border transition-colors
            ${(loading || computing)
              ? 'border-gray-300 dark:border-gray-700 text-gray-400 cursor-wait'
              : isDirty
                ? 'border-brand-gold/60 bg-brand-gold/15 text-brand-gold hover:bg-brand-gold/25'
                : 'border-gray-300 dark:border-gray-700 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
          title={isDirty ? 'Apply pending dropdown changes' : 'Reload (from cache if available)'}
        >
          {loading ? 'loading…' : computing ? 'computing…' : isDirty ? 'apply ↻' : 'reload'}
        </button>
        {vizData && !loading && (
          <div className="flex items-center gap-2 ml-auto text-[11px] text-gray-500">
            {vizData.symbol} · {vizData.idle_deployment === 'cash' ? 'cash idle' : `idle: ${vizData.idle_deployment}`}
            {/* Cached badge + force-recompute link */}
            {vizData.cached && (
              <span className="inline-flex items-center gap-1">
                <span className="px-1.5 py-0.5 rounded text-[10px] bg-blue-500/10 text-blue-500 dark:text-blue-400 border border-blue-500/20">
                  cached
                </span>
                <button
                  onClick={() => fetchViz({ force: true })}
                  className="text-gray-400 hover:text-brand-gold underline underline-offset-2 text-[10px]"
                  title="Discard cached result and recompute from scratch"
                >
                  recompute
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="px-3 py-2 text-xs text-red-700 dark:text-red-300 bg-red-500/10 rounded">
          {error}
        </div>
      )}

      {/* Async compute in progress — shown while Celery task runs (large runs) */}
      {computing && !error && (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-500 dark:text-gray-400">
          <svg className="w-6 h-6 animate-spin text-brand-gold" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
          </svg>
          <div className="text-sm">Computing visualizations…</div>
          <div className="text-xs text-gray-400 dark:text-gray-500">
            First run on large grids takes ~30–60s. Cached afterward.
          </div>
        </div>
      )}

      {!error && !computing && vizData && (
        <>
          {/* When `view` is set to a single chart, render just that chart in
              full width (no card border — caller wraps it). When 'all',
              render the legacy multi-card layout. The tab-driven detail page
              uses single-view; older callers can still use 'all'. */}
          {view === 'all' && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-[1fr,1fr] gap-3">
                <div className="border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-[#161b22] p-3">
                  <div className="flex justify-end mb-2">
                    <Toggle2D3D value={heatmap3d} onChange={setHeatmap3d} />
                  </div>
                  {heatmap3d
                    ? <Surface3DView data={vizData.heatmap} />
                    : <HeatmapView data={vizData.heatmap} />}
                </div>
                <div className="border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-[#161b22] p-3">
                  <HistogramView data={vizData.histogram} />
                </div>
              </div>
              <div className="border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-[#161b22] p-3">
                <div className="flex justify-end mb-2">
                  <ScatterModeToggle value={scatterMode} onChange={setScatterMode} />
                </div>
                {scatterMode === '3d' && <Scatter3DView data={vizData.scatter} />}
                {scatterMode === 'surface' && <Mesh3DView data={vizData.scatter} />}
                {scatterMode === '2d' && <ScatterView data={vizData.scatter} />}
              </div>
              {vizData.stability && Object.keys(vizData.stability).length > 0 && (
                <div className="border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-[#161b22] p-3">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">
                    Stability per parameter (avg {vizData.selected?.metric || 'metric'} as each varies)
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                    {Object.entries(vizData.stability).map(([name, points]) => (
                      <StabilitySpark key={name} name={name} points={points} metric={vizData.selected?.metric} />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {view === 'heatmap' && (
            <>
              <div className="flex justify-end mb-2">
                <Toggle2D3D value={heatmap3d} onChange={setHeatmap3d} />
              </div>
              {heatmap3d
                ? <Surface3DView data={vizData.heatmap} />
                : <HeatmapView data={vizData.heatmap} />}
            </>
          )}

          {view === 'histogram' && (
            <HistogramView data={vizData.histogram} />
          )}

          {view === 'scatter' && (
            <>
              <div className="flex justify-end mb-2">
                <ScatterModeToggle value={scatterMode} onChange={setScatterMode} />
              </div>
              {scatterMode === '3d' && <Scatter3DView data={vizData.scatter} />}
              {scatterMode === 'surface' && <Mesh3DView data={vizData.scatter} />}
              {scatterMode === '2d' && <ScatterView data={vizData.scatter} />}
            </>
          )}

          {view === 'stability' && (
            vizData.stability && Object.keys(vizData.stability).length > 0 ? (
              <>
                <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-3">
                  How avg {vizData.selected?.metric || 'metric'} moves as each parameter sweeps.
                  Flat line = robust (param doesn't matter much). Sharp peak = sweet spot.
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                  {Object.entries(vizData.stability).map(([name, points]) => (
                    <StabilitySpark key={name} name={name} points={points} metric={vizData.selected?.metric} />
                  ))}
                </div>
              </>
            ) : (
              <div className="text-xs text-gray-500 py-8 text-center">No stability data.</div>
            )
          )}
        </>
      )}
    </div>
  );
};

// 2D/3D toggle pill — used by the Heatmap card.
const Toggle2D3D = ({ value, onChange }) => (
  <div className="inline-flex rounded-md border border-gray-200 dark:border-gray-700 text-[10px] overflow-hidden">
    <button
      onClick={() => onChange(false)}
      className={`px-2 py-1 ${!value
        ? 'bg-brand-gold/15 text-brand-gold'
        : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
    >2D</button>
    <button
      onClick={() => onChange(true)}
      className={`px-2 py-1 ${value
        ? 'bg-brand-gold/15 text-brand-gold'
        : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
    >3D</button>
  </div>
);

// Tri-state toggle for the Scatter card — 2D scatter, 3D point cloud, or
// 3D surface (binned grid of the same metric triple).
const ScatterModeToggle = ({ value, onChange }) => {
  const opts = [
    { v: '2d', label: '2D' },
    { v: '3d', label: '3D' },
    { v: 'surface', label: 'Surface' },
  ];
  return (
    <div className="inline-flex rounded-md border border-gray-200 dark:border-gray-700 text-[10px] overflow-hidden">
      {opts.map(o => (
        <button
          key={o.v}
          onClick={() => onChange(o.v)}
          className={`px-2 py-1 ${value === o.v
            ? 'bg-brand-gold/15 text-brand-gold'
            : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
        >{o.label}</button>
      ))}
    </div>
  );
};

export default ChartsPanel;
